import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import DASHBOARD_TOUR_STEPS from './dashboardTourSteps';
import { buildDashboardTourCompletedPatch } from '../../utils/userPreferencesHelpers';
import * as userActions from '../../store/actions/user';

const TOOLTIP_WIDTH = 380;
const TOOLTIP_GAP = 16;
const ARROW_SIZE = 12;
const VIEWPORT_PADDING = 16;

function getTargetRect(selector) {
  const element = document.querySelector(selector);
  if (!element) return null;
  return element.getBoundingClientRect();
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function buildOverlayClipPath(rect) {
  if (!rect) return undefined;
  const top = Math.max(0, rect.top - 8);
  const left = Math.max(0, rect.left - 8);
  const right = Math.min(window.innerWidth, rect.right + 8);
  const bottom = Math.min(window.innerHeight, rect.bottom + 8);
  return `polygon(evenodd, 0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, ${left}px ${top}px, ${right}px ${top}px, ${right}px ${bottom}px, ${left}px ${bottom}px, ${left}px ${top}px)`;
}

function computeLayout(step, targetRect) {
  if (!targetRect) {
    return {
      overlayClipPath: undefined,
      spotlight: null,
      tooltip: {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
      arrow: null,
    };
  }

  const spotlight = {
    top: targetRect.top - 8,
    left: targetRect.left - 8,
    width: targetRect.width + 16,
    height: targetRect.height + 16,
  };
  const overlayClipPath = buildOverlayClipPath(targetRect);

  let tooltipTop = 0;
  let tooltipLeft = 0;
  let arrowStyle = {};

  if (step.placement === 'right') {
    tooltipTop = targetRect.top + targetRect.height / 2;
    tooltipLeft = targetRect.right + TOOLTIP_GAP + ARROW_SIZE;
    arrowStyle = {
      left: -6,
      top: '50%',
      transform: 'translateY(-50%) rotate(45deg)',
    };
  } else if (step.placement === 'bottom') {
    tooltipTop = targetRect.bottom + TOOLTIP_GAP + ARROW_SIZE;
    tooltipLeft = targetRect.left + targetRect.width / 2;
    arrowStyle = {
      left: '50%',
      top: -6,
      transform: 'translateX(-50%) rotate(45deg)',
    };
  } else {
    tooltipTop = targetRect.top - TOOLTIP_GAP - ARROW_SIZE;
    tooltipLeft = targetRect.left + targetRect.width / 2;
    arrowStyle = {
      left: '50%',
      bottom: -6,
      transform: 'translateX(-50%) rotate(45deg)',
    };
  }

  const halfWidth = TOOLTIP_WIDTH / 2;
  const clampedLeft = clamp(tooltipLeft, VIEWPORT_PADDING + halfWidth, window.innerWidth - VIEWPORT_PADDING - halfWidth);
  const clampedTop = clamp(tooltipTop, VIEWPORT_PADDING + 80, window.innerHeight - VIEWPORT_PADDING - 40);

  let transform = 'translate(-50%, -100%)';
  if (step.placement === 'right') {
    transform = 'translate(0, -50%)';
  } else if (step.placement === 'bottom') {
    transform = 'translate(-50%, 0)';
  }

  return {
    overlayClipPath,
    spotlight,
    tooltip: {
      top: clampedTop,
      left: clampedLeft,
      transform,
    },
    arrow: arrowStyle,
  };
}

function TourTooltip({
  step,
  stepIndex,
  totalSteps,
  layout,
  onBack,
  onNext,
  onSkip,
}) {
  const isFirst = stepIndex === 0;
  const isLast = step.isLast || stepIndex === totalSteps - 1;

  return (
    <div className='tour-tip' style={layout.tooltip}>
      {layout.arrow && <div className='tour-arrow' style={layout.arrow} />}
      <button type='button' className='tour-skip' onClick={onSkip}>
        Skip tour
      </button>
      <div className='tour-tip-fade'>
        <h3 className='tour-h'>{step.title}</h3>
        <p className='tour-text'>{step.text}</p>
      </div>
      <div className='tour-foot'>
        <span className='tour-count'>
          {stepIndex + 1} of {totalSteps}
        </span>
        <div className='tour-actions'>
          {!isFirst && (
            <button type='button' className='tour-back' onClick={onBack}>
              Back
            </button>
          )}
          <button type='button' className='tour-next' onClick={onNext}>
            <span>{isLast ? 'Done' : 'Next'}</span>
            {!isLast && (
              <svg viewBox='0 0 24 24' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M5 12h14M13 6l6 6-6 6' />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

TourTooltip.propTypes = {
  step: PropTypes.shape({
    title: PropTypes.node.isRequired,
    text: PropTypes.string.isRequired,
    isLast: PropTypes.bool,
  }).isRequired,
  stepIndex: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  layout: PropTypes.shape({
    tooltip: PropTypes.shape({
      top: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      left: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      transform: PropTypes.string.isRequired,
    }).isRequired,
    arrow: PropTypes.shape({
      top: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      left: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      bottom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      transform: PropTypes.string,
    }),
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

export default function DashboardTour({ active, onClose }) {
  const dispatch = useDispatch();
  const [stepIndex, setStepIndex] = useState(0);
  const [layout, setLayout] = useState({ overlayClipPath: undefined, spotlight: null, tooltip: {}, arrow: null });

  const steps = DASHBOARD_TOUR_STEPS;
  const step = steps[stepIndex];

  const updateLayout = useCallback(() => {
    if (!active || !step) return;
    const element = document.querySelector(step.target);
    if (element) {
      element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    setLayout(computeLayout(step, getTargetRect(step.target)));
  }, [active, step]);

  useLayoutEffect(() => {
    updateLayout();
  }, [updateLayout, stepIndex]);

  useEffect(() => {
    if (!active) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleResize = () => updateLayout();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [active, updateLayout]);

  const finishTour = useCallback(() => {
    dispatch(userActions.upsertUserPreferences(buildDashboardTourCompletedPatch()));
    onClose();
  }, [dispatch, onClose]);

  const handleNext = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      finishTour();
      return;
    }
    setStepIndex((index) => index + 1);
  }, [finishTour, stepIndex, steps.length]);

  const handleBack = useCallback(() => {
    setStepIndex((index) => Math.max(0, index - 1));
  }, []);

  if (!active || !step) return null;

  return createPortal(
    <div className='dashboard-tour-root' role='dialog' aria-modal='true' aria-label='Dashboard tour'>
      <button
        type='button'
        className='dashboard-tour-overlay'
        style={{ clipPath: layout.overlayClipPath }}
        onClick={finishTour}
        aria-label='Close tour'
      />
      {layout.spotlight && (
        <div
          className='dashboard-tour-spotlight'
          style={{
            top: layout.spotlight.top,
            left: layout.spotlight.left,
            width: layout.spotlight.width,
            height: layout.spotlight.height,
          }}
        />
      )}
      <TourTooltip
        step={step}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        layout={layout}
        onBack={handleBack}
        onNext={handleNext}
        onSkip={finishTour}
      />
    </div>,
    document.body,
  );
}

DashboardTour.propTypes = {
  active: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
